import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaPaperPlane, FaPaperclip, FaCheckCircle, FaExchangeAlt,
    FaPhone, FaEnvelope, FaHistory, FaArrowLeft, FaBolt,
    FaTimes, FaGlobeAmericas, FaLaptop, FaExclamationTriangle,
    FaLock, FaShieldAlt, FaTag, FaUser, FaBuilding,
    FaChevronRight, FaClipboardList, FaClock
} from 'react-icons/fa';
import { format, parseISO, differenceInSeconds, addMinutes, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { io } from 'socket.io-client';
import './SupportTicketDetail.css';

// ─── SOCKET ────────────────────────────────────────────────────────────────
const socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001');

// ─── HELPERS ───────────────────────────────────────────────────────────────

/**
 * Formata segundos em texto legível, escalando automaticamente:
 * < 1min  → "45s"
 * < 1h    → "12m 30s"
 * < 1dia  → "3h 15m"
 * < 30d   → "5d 8h"
 * ≥ 30d   → "2m 10d"
 *
 * Resolve o problema de "-2913h 55m" → exibe "-121d 8h"
 */
const formatSLADuration = (totalSeconds) => {
    const abs = Math.abs(totalSeconds);

    if (abs < 60)      return `${abs}s`;
    if (abs < 3600)    return `${Math.floor(abs / 60)}m ${abs % 60}s`;
    if (abs < 86400)   return `${Math.floor(abs / 3600)}h ${Math.floor((abs % 3600) / 60)}m`;
    if (abs < 2592000) return `${Math.floor(abs / 86400)}d ${Math.floor((abs % 86400) / 3600)}h`;

    const months = Math.floor(abs / 2592000);
    const days   = Math.floor((abs % 2592000) / 86400);
    return `${months}m ${days}d`;
};

/** Cor e rótulo do evento de auditoria */
const getAuditStyle = (acao) => {
    const map = {
        'Transferência de Fila': { color: '#3b82f6', bg: '#eff6ff', label: 'Transferência' },
        'Alteração de Status':   { color: '#f59e0b', bg: '#fffbeb', label: 'Status'        },
        'Alteração de Prioridade': { color: '#8b5cf6', bg: '#f5f3ff', label: 'Prioridade'  },
        'Atribuição':             { color: '#14b8a6', bg: '#f0fdfa', label: 'Atribuição'   },
        'Resolução':              { color: '#10b981', bg: '#f0fdf4', label: 'Resolução'    },
    };
    return map[acao] ?? { color: '#64748b', bg: '#f8fafc', label: acao };
};

// ─── COMPONENTE SLA ─────────────────────────────────────────────────────────
const SLAMonitor = ({ criadoEm, prioridade, status, policies }) => {
    const [slaInfo, setSlaInfo] = useState(null);
    const intervalRef = useRef(null);

    const isTerminated = ['Resolvido', 'Fechado'].includes(status);

    useEffect(() => {
        clearInterval(intervalRef.current);

        if (!criadoEm) return;

        if (isTerminated) {
            setSlaInfo({ terminated: true, status });
            return;
        }

        const policy = policies.find(p => p.prioridade === prioridade)
            ?? { tempo_resolucao_minutos: 2880 };
        const totalSecs   = policy.tempo_resolucao_minutos * 60;
        const createdDate = parseISO(criadoEm);
        const deadlineDate = addMinutes(createdDate, policy.tempo_resolucao_minutos);

        const update = () => {
            const now       = new Date();
            const remaining = differenceInSeconds(deadlineDate, now);
            const elapsed   = differenceInSeconds(now, createdDate);
            const percent   = Math.min(100, (elapsed / totalSecs) * 100);
            const overdue   = remaining < 0;

            let color, stateLabel;
            if (overdue)          { color = '#7c3aed'; stateLabel = 'ATRASADO';  }
            else if (percent > 85) { color = '#ef4444'; stateLabel = 'CRÍTICO';   }
            else if (percent > 60) { color = '#f59e0b'; stateLabel = 'ATENÇÃO';   }
            else                   { color = '#10b981'; stateLabel = 'NO PRAZO';  }

            const timeText = overdue
                ? `+${formatSLADuration(Math.abs(remaining))}`
                : formatSLADuration(remaining);

            setSlaInfo({ percent, color, stateLabel, timeText, overdue, remaining, deadlineDate });
        };

        update();
        intervalRef.current = setInterval(update, 1000);

        return () => clearInterval(intervalRef.current);
    }, [criadoEm, prioridade, status, policies]);

    if (!slaInfo) return null;

    // Estado: finalizado
    if (slaInfo.terminated) {
        return (
            <div className="std-sla-widget terminated">
                <div className="std-sla-top">
                    <span className="std-sla-label">SLA · {prioridade}</span>
                    <span className="std-sla-state-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>
                        ENCERRADO
                    </span>
                </div>
                <div className="std-sla-time" style={{ color: '#64748b' }}>Finalizado</div>
            </div>
        );
    }

    return (
        <div className={`std-sla-widget ${slaInfo.overdue ? 'overdue' : ''}`}
             style={{ '--sla-color': slaInfo.color }}>
            <div className="std-sla-top">
                <span className="std-sla-label">SLA · {prioridade}</span>
                <span className="std-sla-state-badge"
                      style={{ background: `${slaInfo.color}18`, color: slaInfo.color }}>
                    {slaInfo.stateLabel}
                </span>
            </div>

            <div className="std-sla-time" style={{ color: slaInfo.color }}>
                {slaInfo.overdue ? 'Atrasado' : 'Restante'}
                <strong> {slaInfo.timeText}</strong>
            </div>

            {/* Barra de progresso */}
            <div className="std-sla-bar-track">
                <div
                    className="std-sla-bar-fill"
                    style={{
                        width:      `${slaInfo.percent}%`,
                        background: slaInfo.color,
                        boxShadow:  `0 0 6px ${slaInfo.color}60`,
                    }}
                />
            </div>

            {/* Prazo absoluto */}
            {isValid(slaInfo.deadlineDate) && (
                <div className="std-sla-deadline">
                    Prazo: {format(slaInfo.deadlineDate, "dd/MM/yy 'às' HH:mm", { locale: ptBR })}
                </div>
            )}
        </div>
    );
};

// ─── COMPONENTE AUDIT DRAWER ─────────────────────────────────────────────────
const AuditDrawer = ({ logs, onClose }) => (
    <>
        <div className="std-drawer-backdrop" onClick={onClose} />
        <aside className="std-audit-drawer">
            <div className="std-drawer-header">
                <div className="std-drawer-title">
                    <FaShieldAlt />
                    <h3>Histórico de Auditoria</h3>
                </div>
                <button className="std-drawer-close" onClick={onClose}><FaTimes /></button>
            </div>

            <div className="std-audit-timeline">
                {logs.length > 0 ? logs.map((log, idx) => {
                    const style = getAuditStyle(log.acao);
                    return (
                        <div key={log.id ?? idx} className="std-audit-item">
                            <div className="std-audit-dot" style={{ background: style.color }} />
                            <div className="std-audit-content">
                                <div className="std-audit-header">
                                    <span className="std-audit-action"
                                          style={{ background: style.bg, color: style.color }}>
                                        {style.label}
                                    </span>
                                    <span className="std-audit-date">
                                        {log.criado_em
                                            ? format(parseISO(log.criado_em), "dd/MM/yy 'às' HH:mm")
                                            : '—'}
                                    </span>
                                </div>
                                <p className="std-audit-detail">{log.detalhes}</p>
                                <div className="std-audit-meta">
                                    <span><FaUser /> {log.usuario_nome}</span>
                                    <span><FaGlobeAmericas /> {log.ip_usuario === '::1' ? '127.0.0.1' : log.ip_usuario}</span>
                                </div>
                            </div>
                        </div>
                    );
                }) : (
                    <div className="std-audit-empty">
                        <FaClipboardList />
                        <span>Nenhuma ação registrada.</span>
                    </div>
                )}
            </div>
        </aside>
    </>
);

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────
export default function SupportTicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Refs
    const scrollRef      = useRef(null);
    const textareaRef    = useRef(null);
    const fileInputRef   = useRef(null);
    const typingTimeout  = useRef(null);

    // State — dados
    const [chamado,     setChamado]     = useState(null);
    const [mensagens,   setMensagens]   = useState([]);
    const [logs,        setLogs]        = useState([]);
    const [scripts,     setScripts]     = useState([]);
    const [causes,      setCauses]      = useState([]);
    const [slaPolicies, setSlaPolicies] = useState([]);

    // State — UI
    const [loading,     setLoading]     = useState(true);
    const [sending,     setSending]     = useState(false);
    const [newMessage,  setNewMessage]  = useState('');
    const [anexo,       setAnexo]       = useState(null);
    const [typingUser,  setTypingUser]  = useState(null);

    // State — modais / drawers
    const [showAuditDrawer,       setShowAuditDrawer]       = useState(false);
    const [showTransferMenu,      setShowTransferMenu]      = useState(false);
    const [showTransferModal,     setShowTransferModal]     = useState(false);
    const [showResolveModal,      setShowResolveModal]      = useState(false);
    const [showScripts,           setShowScripts]           = useState(false);
    const [filteredScripts,       setFilteredScripts]       = useState([]);
    const [variableModal,         setVariableModal]         = useState({ open: false, script: null, vars: [], values: {} });

    // State — dados de formulários
    const [transferData,   setTransferData]   = useState({ dept: '', justificativa: '' });
    const [resolutionData, setResolutionData] = useState({
        causa_id: '', parecer_tecnico: '', solucao_aplicada: ''
    });

    // ── Usuário logado ──────────────────────────────────────────────────
    const user = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user')) || {}; }
        catch { return {}; }
    }, []);

    const isAgent  = ['admin', 'admin_geral', 'suporte', 'operacional', 'financeiro', 'rh']
                        .includes(user.perfil?.toLowerCase());

    // Chamado fechado: sem interação possível
    const isClosed = useMemo(() =>
        ['Resolvido', 'Fechado'].includes(chamado?.status),
    [chamado?.status]);

    // ── Scroll ──────────────────────────────────────────────────────────
    const scrollToBottom = useCallback(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, []);

    // ── Socket.IO ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!id) return;

        socket.emit('join_support_ticket', id);

        socket.on('new_support_message', (msg) => {
            setMensagens(prev =>
                prev.find(m => m.id === msg.id) ? prev : [...prev, msg]
            );
            setTimeout(scrollToBottom, 50);
        });

        socket.on('support_ticket_updated', (update) => {
            if (update.chamadoId === id || update.chamadoId === String(id)) {
                setChamado(prev => prev ? { ...prev, ...update } : prev);
            }
        });

        socket.on('user_typing', ({ userNome, isTyping }) => {
            if (userNome !== user.nome) setTypingUser(isTyping ? userNome : null);
        });

        return () => {
            socket.off('new_support_message');
            socket.off('support_ticket_updated');
            socket.off('user_typing');
            socket.emit('leave_support_ticket', id);
        };
    }, [id, user.nome, scrollToBottom]);

    // ── Fetch inicial ────────────────────────────────────────────────────
    useEffect(() => {
        const fetchAll = async () => {
            try {
                const endpoint = isAgent
                    ? `/api/admin/suporte/chamados/${id}`
                    : `/api/suporte/chamados/${id}`;

                const [resChamado, resScripts, resCausas, resSla, resLogs] = await Promise.all([
                    api.get(endpoint),
                    isAgent ? api.get('/api/admin/suporte-config/scripts')     : Promise.resolve({ data: [] }),
                    isAgent ? api.get('/api/admin/suporte-config/causas')      : Promise.resolve({ data: [] }),
                    isAgent ? api.get('/api/admin/suporte-config/sla-politicas') : Promise.resolve({ data: [] }),
                    isAgent ? api.get(`/api/admin/suporte/chamados/${id}/logs`) : Promise.resolve({ data: [] }),
                ]);

                if (resChamado.data) {
                    setChamado(resChamado.data);
                    setMensagens(resChamado.data.mensagens || []);
                }

                setLogs(resLogs.data       || []);
                setScripts(resScripts.data || []);
                setCauses(resCausas.data   || []);
                setSlaPolicies(resSla.data || []);
                setLoading(false);
                setTimeout(scrollToBottom, 120);
            } catch (err) {
                console.error('Erro ao carregar chamado:', err);
                toast.error('Erro ao carregar o chamado.');
                setLoading(false);
            }
        };

        fetchAll();
    }, [id, isAgent, scrollToBottom]);

    // ── Input / Scripts ──────────────────────────────────────────────────
    const handleInputChange = (e) => {
        const val = e.target.value;
        setNewMessage(val);

        socket.emit('typing_status', { ticketId: id, userNome: user.nome, isTyping: true });
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => {
            socket.emit('typing_status', { ticketId: id, userNome: user.nome, isTyping: false });
        }, 2500);

        if (val.includes('/')) {
            const cmd = val.split('/').pop().toLowerCase();
            const filtered = scripts.filter(
                s => s.cmd.toLowerCase().includes('/' + cmd) || s.label.toLowerCase().includes(cmd)
            );
            setFilteredScripts(filtered);
            setShowScripts(filtered.length > 0);
        } else {
            setShowScripts(false);
        }
    };

    const applyScriptText = (text) => {
        const parts = newMessage.split('/');
        parts.pop();
        setNewMessage(parts.join('/') + text);
        setShowScripts(false);
        textareaRef.current?.focus();
    };

    const triggerScript = (script) => {
        const matches = [...script.text.matchAll(/\{(\w+)\}/g)];
        const vars = matches.map(m => m[1]);
        if (vars.length > 0) {
            setVariableModal({ open: true, script, vars, values: {} });
            setShowScripts(false);
        } else {
            applyScriptText(script.text);
        }
    };

    // ── Enviar mensagem ──────────────────────────────────────────────────
    const handleSendMessage = async () => {
        if (isClosed || (!newMessage.trim() && !anexo)) return;
        setSending(true);
        socket.emit('typing_status', { ticketId: id, userNome: user.nome, isTyping: false });

        try {
            const formData = new FormData();
            formData.append('texto', newMessage);
            if (anexo) formData.append('anexo', anexo);

            const { data: novaMsg } = await api.post(`/api/suporte/chamados/${id}/mensagens`, formData);
            setMensagens(prev => [...prev, novaMsg]);
            setNewMessage('');
            setAnexo(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            scrollToBottom();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao enviar mensagem.');
        } finally {
            setSending(false);
        }
    };

    // ── Transferência ────────────────────────────────────────────────────
    const handleOpenTransfer = (dept) => {
        const primeiroNome = (chamado?.criado_por_nome || 'Cliente').split(' ')[0];
        const scriptTS     = scripts.find(s => s.cmd === '/ts');
        const texto        = scriptTS
            ? scriptTS.text.replace(/{nome}/g, primeiroNome)
            : `Olá ${primeiroNome}, seu chamado está sendo transferido para a equipe de ${dept.toUpperCase()} para melhor suporte.`;

        setTransferData({ dept, justificativa: texto });
        setShowTransferMenu(false);
        setShowTransferModal(true);
    };

    const handleConfirmTransfer = async () => {
        if (!transferData.justificativa.trim() || transferData.justificativa.length < 5) {
            return toast.warn('Forneça uma justificativa válida (mínimo 5 caracteres).');
        }
        try {
            await api.put(`/api/admin/suporte/chamados/${id}/details`, {
                perfil_destino: transferData.dept,
                justificativa:  transferData.justificativa
            });
            toast.success(`Ticket transferido para a fila: ${transferData.dept.toUpperCase()}`);
            setShowTransferModal(false);

            if (!['admin', 'admin_geral'].includes(user.perfil?.toLowerCase())) {
                navigate('/suporte-admin');
            } else {
                window.location.reload();
            }
        } catch (err) {
            toast.error(err.response?.data?.error || 'Falha ao transferir.');
        }
    };

    // ── Resolução ────────────────────────────────────────────────────────
    const handleResolveSubmit = async () => {
        if (!resolutionData.causa_id || !resolutionData.parecer_tecnico || !resolutionData.solucao_aplicada) {
            return toast.warn('Preencha todos os campos obrigatórios.');
        }
        try {
            await api.put(`/api/admin/suporte/chamados/${id}/details`, {
                status: 'Resolvido',
                ...resolutionData
            });
            setChamado(prev => ({ ...prev, status: 'Resolvido' }));
            toast.success('Chamado marcado como Resolvido!');
            setShowResolveModal(false);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao encerrar.');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="std-loading-screen">
                <FaBolt className="std-pulse" />
                <span>Carregando cockpit de suporte…</span>
            </div>
        );
    }

    if (!chamado) {
        return (
            <div className="std-loading-screen">
                <FaExclamationTriangle style={{ color: '#ef4444' }} />
                <span>Chamado não encontrado.</span>
                <button className="std-btn-ghost" onClick={() => navigate(-1)}>Voltar</button>
            </div>
        );
    }

    return (
        <div className="std-container">

            {/* ═══ SIDEBAR ESQUERDA ═════════════════════════════════════ */}
            <aside className="std-sidebar-left">

                {/* Navegação */}
                <div className="std-nav-header">
                    <button className="std-icon-btn back" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <div>
                        <span className="std-nav-title">#{chamado.id}</span>
                        {isClosed && (
                            <span className="std-closed-badge">
                                <FaLock /> {chamado.status}
                            </span>
                        )}
                    </div>
                </div>

                {/* SLA Monitor */}
                <SLAMonitor
                    criadoEm={chamado.criado_em}
                    prioridade={chamado.prioridade}
                    status={chamado.status}
                    policies={slaPolicies}
                />

                {/* Status e Prioridade */}
                <div className="std-status-card">
                    <div className="std-status-indicator">
                        <span className={`std-status-dot status-${chamado.status.toLowerCase().replace(/ /g, '-')}`} />
                        <span className="std-status-text">{chamado.status}</span>
                    </div>
                    <span className={`std-prio-tag prio-${chamado.prioridade?.toLowerCase()}`}>
                        {chamado.prioridade}
                    </span>
                </div>

                {/* Fila e ações (apenas quando não fechado) */}
                {isAgent && (
                    <div className="std-actions-menu">
                        <label>Fila de Atendimento</label>
                        <div className="std-team-selector">
                            <span className="std-current-team">{chamado.perfil_destino || '—'}</span>
                            {!isClosed && (
                                <button
                                    className="std-icon-btn small"
                                    onClick={() => setShowTransferMenu(v => !v)}
                                    title="Transferir fila"
                                >
                                    <FaExchangeAlt />
                                </button>
                            )}
                        </div>

                        {showTransferMenu && !isClosed && (
                            <div className="std-popover-menu">
                                <div className="std-popover-header">Transferir para:</div>
                                {['suporte', 'financeiro', 'operacional', 'rh'].map(dept => (
                                    <button
                                        key={dept}
                                        className="std-popover-item"
                                        onClick={() => handleOpenTransfer(dept)}
                                    >
                                        <FaChevronRight style={{ fontSize: '0.65rem', opacity: 0.5 }} />
                                        {dept}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="std-divider-h" />

                        {!isClosed ? (
                            <button className="std-primary-btn resolve" onClick={() => setShowResolveModal(true)}>
                                <FaCheckCircle /> Resolver Ticket
                            </button>
                        ) : (
                            <div className="std-closed-notice">
                                <FaLock />
                                <span>Chamado encerrado — somente leitura</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Rodapé com metadados */}
                <div className="std-meta-footer">
                    <div className="std-meta-row">
                        <FaTag />
                        <span>{chamado.categoria_nome || '—'} {chamado.subcategoria_nome ? `› ${chamado.subcategoria_nome}` : ''}</span>
                    </div>
                    <div className="std-meta-row">
                        <FaClock />
                        <span>Aberto em {chamado.criado_em ? format(parseISO(chamado.criado_em), "dd/MM/yyyy 'às' HH:mm") : '—'}</span>
                    </div>
                    {chamado.data_resolucao && (
                        <div className="std-meta-row">
                            <FaCheckCircle style={{ color: '#10b981' }} />
                            <span>Resolvido em {format(parseISO(chamado.data_resolucao), "dd/MM/yyyy 'às' HH:mm")}</span>
                        </div>
                    )}
                </div>
            </aside>

            {/* ═══ CHAT PRINCIPAL ═══════════════════════════════════════ */}
            <main className="std-main-chat">

                {/* Header do chat */}
                <header className="std-chat-header">
                    <div className="std-header-info">
                        <h2>{chamado.assunto}</h2>
                        <span className="std-breadcrumb">
                            {chamado.categoria_nome || 'Sem categoria'}
                            {chamado.subcategoria_nome && ` / ${chamado.subcategoria_nome}`}
                        </span>
                    </div>
                </header>

                {/* Banner de chamado fechado */}
                {isClosed && (
                    <div className="std-closed-banner">
                        <FaLock />
                        <div>
                            <strong>Chamado {chamado.status}</strong>
                            <span>Este ticket está encerrado. Não é possível adicionar novas mensagens.</span>
                        </div>
                    </div>
                )}

                {/* Área de mensagens */}
                <div className="std-messages-area" ref={scrollRef}>
                    {mensagens.map(msg => {
                        const isMe     = Number(msg.remetente_id) === Number(user.id);
                        const isSystem = msg.remetente_tipo === 'system';

                        if (isSystem) {
                            return (
                                <div key={msg.id ?? Math.random()} className="system-row">
                                    <div className="std-system-msg">
                                        <FaHistory />
                                        <span>{msg.texto}</span>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={msg.id} className={`std-msg-row ${isMe ? 'me' : 'other'}`}>
                                {!isMe && (
                                    <div className="std-avatar">
                                        {msg.remetente_nome?.substring(0, 2).toUpperCase()}
                                    </div>
                                )}
                                <div className="std-msg-bubble">
                                    <div className="std-msg-name">{msg.remetente_nome}</div>
                                    <div className="std-msg-text">{msg.texto}</div>
                                    {msg.anexo_url && (
                                        <a href={msg.anexo_url} target="_blank" rel="noreferrer" className="std-attachment-card">
                                            <div className="std-att-icon"><FaPaperclip /></div>
                                            <div className="std-att-info">
                                                <span className="std-att-name">{msg.anexo_nome || 'Anexo'}</span>
                                                <span className="std-att-type">Ver arquivo</span>
                                            </div>
                                        </a>
                                    )}
                                    <div className="std-msg-meta">
                                        {msg.criado_em ? format(parseISO(msg.criado_em), 'HH:mm') : ''}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {typingUser && (
                        <div className="std-typing-indicator">
                            <div className="typing-dots">
                                <span /><span /><span />
                            </div>
                            <small>{typingUser} está digitando…</small>
                        </div>
                    )}
                </div>

                {/* Input — oculto quando fechado */}
                {!isClosed && (
                    <div className="std-input-wrapper">
                        {showScripts && (
                            <div className="std-scripts-popup">
                                <div className="std-scripts-title">Sugestões rápidas</div>
                                {filteredScripts.map(s => (
                                    <div key={s.id} className="std-script-option" onClick={() => triggerScript(s)}>
                                        <span className="std-script-cmd">{s.cmd}</span>
                                        <span>{s.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="std-input-container">
                            {anexo && (
                                <div className="std-upload-preview">
                                    <FaPaperclip />
                                    <span>{anexo.name}</span>
                                    <button onClick={() => { setAnexo(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                                        <FaTimes />
                                    </button>
                                </div>
                            )}
                            <button className="std-tool-btn" onClick={() => fileInputRef.current?.click()}>
                                <FaPaperclip />
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={e => setAnexo(e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                            <textarea
                                ref={textareaRef}
                                className="std-textarea"
                                value={newMessage}
                                onChange={handleInputChange}
                                placeholder="Sua resposta… (/ para scripts rápidos)"
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                            />
                            <button
                                className="std-send-btn"
                                onClick={handleSendMessage}
                                disabled={sending || (!newMessage.trim() && !anexo)}
                            >
                                {sending ? '…' : <FaPaperPlane />}
                            </button>
                        </div>
                    </div>
                )}
            </main>

            {/* ═══ SIDEBAR DIREITA ══════════════════════════════════════ */}
            <aside className="std-sidebar-right">

                {/* Perfil do solicitante */}
                <div className="std-user-profile">
                    <div className="std-user-avatar-lg">
                        {chamado.criado_por_nome?.substring(0, 2).toUpperCase()}
                    </div>
                    <h3>{chamado.criado_por_nome}</h3>
                    <div className="std-user-status">{chamado.nome_empresa || 'Interno'}</div>
                </div>

                {/* Contato */}
                <div className="std-info-section">
                    <h4>Contato</h4>
                    <div className="std-info-item">
                        <FaEnvelope />
                        <span className="std-info-text">
                            {chamado.solicitante_email || chamado.criado_por_email || '—'}
                        </span>
                    </div>
                    <div className="std-info-item">
                        <FaPhone />
                        <span className="std-info-text">{chamado.solicitante_telefone || 'N/A'}</span>
                    </div>
                    {chamado.nome_empresa && (
                        <div className="std-info-item">
                            <FaBuilding />
                            <span className="std-info-text">{chamado.nome_empresa}</span>
                        </div>
                    )}
                </div>

                {/* Contexto técnico */}
                <div className="std-info-section">
                    <h4>Contexto Técnico</h4>
                    <div className="std-device-info">
                        <div className="device-item">
                            <FaLaptop />
                            <span>GuinchoERP CRM</span>
                        </div>
                        <div className="device-item">
                            <FaGlobeAmericas />
                            <span>IP: {chamado.ip_origem || 'Interno'}</span>
                        </div>
                        {chamado.tipo && (
                            <div className="device-item">
                                <FaTag />
                                <span>Tipo: {chamado.tipo}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Informações de resolução (se resolvido) */}
                {chamado.causa_raiz_descricao && (
                    <div className="std-info-section">
                        <h4>Resolução</h4>
                        <div className="std-resolution-card">
                            {chamado.causa_raiz_descricao && (
                                <div className="std-res-row">
                                    <span className="std-res-label">Causa Raiz</span>
                                    <span>{chamado.causa_raiz_descricao}</span>
                                </div>
                            )}
                            {chamado.parecer_tecnico && (
                                <div className="std-res-row">
                                    <span className="std-res-label">Parecer Técnico</span>
                                    <span>{chamado.parecer_tecnico}</span>
                                </div>
                            )}
                            {chamado.solucao_aplicada && (
                                <div className="std-res-row">
                                    <span className="std-res-label">Solução</span>
                                    <span>{chamado.solucao_aplicada}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Botão de auditoria */}
                {isAgent && (
                    <button
                        className="std-audit-trigger-btn"
                        onClick={() => setShowAuditDrawer(true)}
                    >
                        <div className="std-audit-trigger-left">
                            <FaShieldAlt />
                            <div>
                                <span>Histórico de Auditoria</span>
                                <small>{logs.length} {logs.length === 1 ? 'registro' : 'registros'}</small>
                            </div>
                        </div>
                        <FaChevronRight />
                    </button>
                )}
            </aside>

            {/* ═══ AUDIT DRAWER ═════════════════════════════════════════ */}
            {showAuditDrawer && (
                <AuditDrawer
                    logs={logs}
                    onClose={() => setShowAuditDrawer(false)}
                />
            )}

            {/* ═══ MODAIS ══════════════════════════════════════════════ */}

            {/* Modal: Transferência */}
            {showTransferModal && (
                <div className="std-modal-overlay">
                    <div className="std-modal-card">
                        <div className="std-modal-header">
                            <h3>Handover de Atendimento</h3>
                            <button onClick={() => setShowTransferModal(false)}><FaTimes /></button>
                        </div>
                        <div className="std-modal-body">
                            <div className="std-handover-alert">
                                <FaExchangeAlt />
                                <span>
                                    Transferindo para a fila:{' '}
                                    <strong>{transferData.dept.toUpperCase()}</strong>
                                </span>
                            </div>
                            <div className="std-form-group">
                                <label>Justificativa *</label>
                                <textarea
                                    className="std-textarea-bordered"
                                    rows={5}
                                    value={transferData.justificativa}
                                    onChange={e => setTransferData({ ...transferData, justificativa: e.target.value })}
                                    placeholder="Explique o motivo da transferência…"
                                />
                                <small>Esta mensagem será registrada no histórico para auditoria.</small>
                            </div>
                        </div>
                        <div className="std-modal-footer">
                            <button className="std-btn-ghost" onClick={() => setShowTransferModal(false)}>Cancelar</button>
                            <button className="std-btn-primary" onClick={handleConfirmTransfer}>
                                Confirmar Transferência
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Variáveis de script */}
            {variableModal.open && (
                <div className="std-modal-overlay">
                    <div className="std-modal-card">
                        <div className="std-modal-header">
                            <h3>Preencher Variáveis</h3>
                            <button onClick={() => setVariableModal({ open: false, script: null, vars: [], values: {} })}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="std-modal-body">
                            {variableModal.vars.map(v => (
                                <div key={v} className="std-form-group">
                                    <label>{v}</label>
                                    <input
                                        className="std-input"
                                        placeholder={`Valor para {${v}}`}
                                        onChange={e => setVariableModal(prev => ({
                                            ...prev,
                                            values: { ...prev.values, [v]: e.target.value }
                                        }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="std-modal-footer">
                            <button className="std-btn-ghost" onClick={() => setVariableModal({ open: false, script: null, vars: [], values: {} })}>
                                Cancelar
                            </button>
                            <button className="std-btn-primary" onClick={() => {
                                let text = variableModal.script.text;
                                variableModal.vars.forEach(v => {
                                    text = text.replace(`{${v}}`, variableModal.values[v] || '');
                                });
                                applyScriptText(text);
                                setVariableModal({ open: false, script: null, vars: [], values: {} });
                            }}>
                                Inserir no Chat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Finalizar atendimento */}
            {showResolveModal && (
                <div className="std-modal-overlay">
                    <div className="std-modal-card large">
                        <div className="std-modal-header">
                            <h3>Finalizar Atendimento</h3>
                            <button onClick={() => setShowResolveModal(false)}><FaTimes /></button>
                        </div>
                        <div className="std-resolve-form">
                            <div className="std-form-group">
                                <label>Causa Raiz *</label>
                                <select
                                    className="std-input"
                                    value={resolutionData.causa_id}
                                    onChange={e => setResolutionData({ ...resolutionData, causa_id: e.target.value })}
                                >
                                    <option value="">Selecione a causa identificada…</option>
                                    {causes.map(c => (
                                        <option key={c.id} value={c.id}>{c.descricao}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="std-form-group">
                                <label>Parecer Técnico *</label>
                                <textarea
                                    className="std-textarea-bordered"
                                    rows={3}
                                    value={resolutionData.parecer_tecnico}
                                    onChange={e => setResolutionData({ ...resolutionData, parecer_tecnico: e.target.value })}
                                    placeholder="Descrição técnica do que foi diagnosticado…"
                                />
                            </div>
                            <div className="std-form-group">
                                <label>Solução Aplicada *</label>
                                <textarea
                                    className="std-textarea-bordered"
                                    rows={3}
                                    value={resolutionData.solucao_aplicada}
                                    onChange={e => setResolutionData({ ...resolutionData, solucao_aplicada: e.target.value })}
                                    placeholder="Quais ações foram tomadas para resolver?"
                                />
                            </div>
                        </div>
                        <div className="std-modal-footer">
                            <button className="std-btn-ghost" onClick={() => setShowResolveModal(false)}>Voltar</button>
                            <button className="std-btn-primary" onClick={handleResolveSubmit}>
                                <FaCheckCircle /> Encerrar Chamado
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}